import type { RefObject } from 'react';
import { useImperativeHandle, useMemo, useRef } from 'react';

import moment from 'moment';

import { parseAbsoluteToLocal } from '@internationalized/date';
import type { ZonedDateTime } from '@internationalized/date';

import type { SegmentType } from '@react-stately/datepicker';

import type { DOMRef, DOMRefValue, FocusableRef, FocusableRefValue } from '@react-types/shared';

export function createDOMRef<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>
): DOMRefValue<T> {
  return {
    UNSAFE_getDOMNode() {
      return ref.current;
    }
  };
}

export function createFocusableRef<T extends HTMLElement = HTMLElement>(
  domRef: RefObject<T>,
  focusableRef: RefObject<HTMLElement> = domRef
): FocusableRefValue<T> {
  return {
    ...createDOMRef(domRef),
    focus() {
      if (focusableRef.current) {
        focusableRef.current.focus();
      }
    }
  };
}

export function useDOMRef<T extends HTMLElement = HTMLElement>(ref: DOMRef<T>): RefObject<T> {
  let domRef = useRef<T>(null);
  useImperativeHandle(ref, () => createDOMRef(domRef));
  return domRef;
}

export function useFocusableRef<T extends HTMLElement = HTMLElement>(
  ref: FocusableRef<T>,
  focusableRef?: RefObject<HTMLElement>
): RefObject<T> {
  let domRef = useRef<T>(null);
  useImperativeHandle(ref, () => createFocusableRef(domRef, focusableRef));
  return domRef;
}

export function unwrapDOMRef<T extends HTMLElement>(ref: RefObject<DOMRefValue<T>>): RefObject<T> {
  return {
    get current() {
      return ref.current && ref.current.UNSAFE_getDOMNode();
    }
  };
}

export function useUnwrapDOMRef<T extends HTMLElement>(
  ref: RefObject<DOMRefValue<T>>
): RefObject<T> {
  return useMemo(() => unwrapDOMRef(ref), [ref]);
}

export function getDateTimeFormat(segmentType: SegmentType) {
  switch (segmentType) {
    case 'year':
      return 'YYYY';

    case 'month':
      return 'MM';

    case 'day':
      return 'DD';

    default:
      return '';
  }
}

export const toTZDate = (date: moment.MomentInput): ZonedDateTime => {
  return parseAbsoluteToLocal(moment(date).toISOString(false));
};
